import { isRunningInIosNativeApp } from '~/utils/browserInfo';
import { useSiteStatusStore } from '~/stores/site-status';

export default function useFeatureFlagsNew() {
  const { isLoading: isLoadingFeatures, features: featuresFromStore } =
    storeToRefs(useFeaturesStore());

  const { visitorOrUserCountry } = useParameters();
  const { userId } = useUserData();

  // FUNCTIONS

  async function fetchFeaturesFromApi() {
    isLoadingFeatures.value = true;

    try {
      const { ApiService } = useApiService();

      await ApiService.get(`/bff/features`)
        .then(({ data }: any) => {
          featuresFromStore.value = data.data as FeaturesFromApi[];
        })
        .catch((error: any) => {
          console.error('fetchFeaturesFromApi error: ', error);
        })
        .finally(() => {
          isLoadingFeatures.value = false;
        });
    } catch (err) {
      console.error('fetchFeaturesFromApi err: ', err);
      isLoadingFeatures.value = false;
    }
  }

  async function fetchEmailUpdateFeatureFlag(): Promise<boolean> {
    try {
      const { ApiService } = useApiService();
      const { data } = await ApiService.get(
        '/bff/users/validate-email-update-enable',
      );

      return Boolean(data?.enable_email_update);
    } catch (error) {
      console.error('fetchEmailUpdateFeatureFlag error: ', error);

      return false;
    }
  }

  const getFeaturesByCountry = (country: string): FeaturesFromApi => {
    if (!Array.isArray(featuresFromStore.value)) {
      return featuresFromStore.value;
    }

    const defaultFeature =
      featuresFromStore.value.find(({ is_default }: any) => !!is_default) ||
      featuresFromStore.value[0];

    if (!!country === false) {
      return defaultFeature as FeaturesFromApi;
    } else {
      const featureByCountry = featuresFromStore.value.find(
        (feature: any) => feature.country === country,
      );

      return featureByCountry || (defaultFeature as FeaturesFromApi);
    }
  };

  const featuresFromApi = computed<FeaturesFromApi>(() => {
    return getFeaturesByCountry(visitorOrUserCountry.value);
  });

  function isUserAllowed(testUsers: string): boolean {
    if (testUsers === '0') {
      return true;
    }

    const usersList = computed(() => {
      return typeof testUsers === 'string' ? testUsers.split(',') : testUsers;
    });

    if (!usersList.value || usersList.value.length === 0) {
      return true;
    }

    return usersList.value.includes(String(userId.value));
  }

  function isFeatureFromApiEnabled(key: keyof FeaturesFromApi): boolean {
    if (!featuresFromApi.value) {
      return false;
    }

    try {
      return !!featuresFromApi.value[key];
    } catch (error) {
      console.warn(`useFeatureFlagsNew: ${key} not found on featuresFromApi`);

      return false;
    }
  }

  function getStringValueFromApi(key: keyof FeaturesFromApi): string {
    if (!isFeatureFromApiEnabled(key)) {
      return '';
    }

    return featuresFromApi.value[key] as string;
  }

  function getFullArrayFromApi<T = WidgetsEnabled | Containment.PendencyName>(
    key: keyof FeaturesFromApi,
  ): T[] {
    const stringValue = getStringValueFromApi(key);

    if (!stringValue) {
      return [];
    }

    return normalizeStringJsonArray(stringValue) as T[];
  }

  function getFullObjectFromApi<T = AuthConfigs>(
    key: keyof FeaturesFromApi,
  ): T {
    const stringValue = getStringValueFromApi(key);

    if (!stringValue) {
      return {} as T;
    }

    return normalizeStringJsonObject(stringValue) as T;
  }

  // FEATURES

  const widgetsEnabled = computed<WidgetsEnabled[]>(() => {
    return getFullArrayFromApi<WidgetsEnabled>('widgets_enabled');
  });

  const isLegalAgePopupEnabled = computed<boolean>(() => {
    return widgetsEnabled.value.includes('legal_age_popup');
  });

  const isTopbarUpToFiveItemsEnabled = computed<boolean>(() => {
    return widgetsEnabled.value.includes('topbar_multiple');
  });

  const isInstallAppPopupEnabled = computed<boolean>(() => {
    return widgetsEnabled.value.includes('install_app');
  });

  const isSportsSearchEnabled = computed<boolean>(() => {
    return widgetsEnabled.value.includes('sports_search');
  });

  const isSportsWidgetEnabled = computed<boolean>(() => {
    return widgetsEnabled.value.includes('sports_widget');
  });

  const isBiggestWinnersCarouselEnabled = computed<boolean>(() => {
    return widgetsEnabled.value.includes('biggest_winners');
  });

  const isRecentWinnersCarouselEnabled = computed<boolean>(() => {
    return widgetsEnabled.value.includes('recent_winners');
  });

  const isGameStatisticsEnabled = computed<boolean>(() => {
    return widgetsEnabled.value.includes('game_statistics');
  });

  const authConfigsFromApi = computed<AuthConfigs>(() => {
    return getFullObjectFromApi<AuthConfigs>('auth_configs');
  });

  const isGoogleButtonActive = computed<boolean>(() => {
    return isFeatureFromApiEnabled('social_auth_google_active');
  });

  const isFacebookButtonActive = computed<boolean>(() => {
    return isFeatureFromApiEnabled('social_auth_facebook_active');
  });

  const isTwitchButtonActive = computed<boolean>(() => {
    return isFeatureFromApiEnabled('social_auth_twitch_active');
  });

  const isSteamButtonActive = computed<boolean>(() => {
    return isFeatureFromApiEnabled('social_auth_steam_active');
  });

  const isSocialAuthEnabled = computed<boolean>(() => {
    // A App Store (Guideline 4.8) exige uma opção de login que preserve a
    // privacidade (ex.: Sign in with Apple) sempre que houver login social.
    // Como o backend é white-label e não permite adicionar o SIWA, o login
    // social é ocultado dentro do app iOS — resta o login por e-mail/senha e
    // a 4.8 deixa de se aplicar. Android e navegador não são afetados.
    if (isRunningInIosNativeApp()) {
      return false;
    }

    return !!(
      isGoogleButtonActive.value ||
      isFacebookButtonActive.value ||
      isTwitchButtonActive.value ||
      isSteamButtonActive.value
    );
  });

  const isLudopathyValidationEnabled = computed<boolean>(() => {
    return authConfigsFromApi.value?.enable_ludopathy_validation ?? false;
  });

  const isAccountSetLimitsEnabled = computed<boolean>(() => {
    return (
      isFeatureFromApiEnabled('account_set_limits_active') &&
      isUserAllowed(featuresFromApi.value.account_set_limits_users)
    );
  });

  const isAccountTimeoutLimitsEnabled = computed<boolean>(() => {
    return (
      isFeatureFromApiEnabled('account_timeout_limits_active') &&
      isUserAllowed(featuresFromApi.value.account_timeout_limits_users)
    );
  });

  const isCookieConsentPopUpEnabled = computed<boolean>(() => {
    return isFeatureFromApiEnabled('active_cookie_consent_popup');
  });

  const isNewAuthButtonsEnabled = computed<boolean>(() => {
    return isFeatureFromApiEnabled('new_auth_buttons_active');
  });

  const isNewAuthButtonsPillEnabled = computed<boolean>(() => {
    return isFeatureFromApiEnabled('new_auth_buttons_pill_active');
  });

  const isNewDepositButtonPillEnabled = computed<boolean>(() => {
    return isNewAuthButtonsEnabled.value && isNewAuthButtonsPillEnabled.value;
  });

  const isDemoModeEnabled = computed<boolean>(() => {
    return (
      isFeatureFromApiEnabled('games_demo_mode_active') &&
      isUserAllowed(featuresFromApi.value.games_demo_mode_test_users)
    );
  });

  const isRTPEnabled = computed<boolean>(() => {
    return (
      isFeatureFromApiEnabled('games_show_rtp_info_active') &&
      isUserAllowed(featuresFromApi.value.games_show_rtp_info_test_users)
    );
  });

  const nomenclatureForCasinoGames = computed<string>(() => {
    const nomenclature = getStringValueFromApi('nomenclature_for_casino_games');

    if (
      nomenclature !== 'casino' &&
      nomenclature !== 'games' &&
      nomenclature !== 'ilottery'
    ) {
      return 'casino';
    }

    return nomenclature || 'casino';
  });

  const brandContacts = computed<FeatureContacts>(() => {
    const contactsFromApi = getStringValueFromApi('contacts');

    if (!contactsFromApi) {
      return {} as FeatureContacts;
    }

    return normalizeStringJsonObject(contactsFromApi) as FeatureContacts;
  });

  const companyData = computed<FeatureCompanyData>(() => {
    const companyDataFromApi = getStringValueFromApi('company_data');

    if (!companyDataFromApi) {
      return {} as FeatureCompanyData;
    }

    return normalizeStringJsonObject(companyDataFromApi) as FeatureCompanyData;
  });

  const isHomeAsSportbookEnabled = computed<boolean>(() => {
    const { homeAsSportbook } = useRuntimeConfig().public.featureFlags;

    return (
      homeAsSportbook || isFeatureFromApiEnabled('home_as_sportbook_active')
    );
  });

  const isMaintenanceModeEnabled = computed<boolean>(() => {
    const { maintenanceMode } = useRuntimeConfig().public.featureFlags;

    return (
      maintenanceMode || isFeatureFromApiEnabled('maintenance_mode_active')
    );
  });

  // Operações do site (ENG-915): cada parte que a pausa da Medida Provisória
  // tira do ar tem o seu switch no core (GET /api/site-status), e cada getter lê
  // só o seu — ligar um não mexe nos outros. Sem resposta do core vale a última
  // boa, e sem nenhuma o site fica pausado (stores/site-status.ts).
  const siteStatus = computed(() => useSiteStatusStore().status);

  const isHomeNoticeEnabled = computed<boolean>(
    () => siteStatus.value.homeNoticeEnabled,
  );

  const isGamesPaused = computed<boolean>(() => !siteStatus.value.gamesEnabled);

  const isDepositPaused = computed<boolean>(
    () => !siteStatus.value.depositsEnabled,
  );

  const isRegisterPaused = computed<boolean>(
    () => !siteStatus.value.registrationsEnabled,
  );

  const isCheckinPaused = computed<boolean>(
    () => !siteStatus.value.dailyCheckInEnabled,
  );

  const isRoulettePaused = computed<boolean>(
    () => !siteStatus.value.rouletteEnabled,
  );

  // Desligamento geral: só a landing aparece. `!== false`, e não o valor
  // direto: um status persistido antes deste campo existir também desliga, e
  // o core fora do ar nunca reabre o site.
  const isSiteShutdown = computed<boolean>(
    () => siteStatus.value.siteShutdown !== false,
  );

  const isContractsSectionEnabled = computed<boolean>(() => {
    return isFeatureFromApiEnabled('active_contracts_section');
  });

  const isUserMigrationEnabled = computed<boolean>(() => {
    // return false;

    return isFeatureFromApiEnabled('active_user_migration');
  });

  const userMigrationNewDomain = computed<string>(() => {
    // todo: use this fields to redirect user after login
    return getStringValueFromApi('migration_new_domain');
  });

  const userMigrationDate = computed<string>(() => {
    const { forceMigrationDate } = useRuntimeConfig().public;

    return forceMigrationDate || getStringValueFromApi('migration_date');
  });

  const isWithinMigrationPeriod = computed<boolean>(() => {
    if (!isUserMigrationEnabled.value) {
      return false;
    }

    const dateNow = new Date();

    const userMigrationDateValue = new Date(
      `${userMigrationDate.value}T00:00:00`,
    );

    return dateNow > userMigrationDateValue;
  });

  return {
    fetchFeaturesFromApi,
    fetchEmailUpdateFeatureFlag,
    featuresFromApi,
    isFeatureFromApiEnabled,
    getStringValueFromApi,
    getFullArrayFromApi,
    getFullObjectFromApi,
    isLegalAgePopupEnabled,
    isTopbarUpToFiveItemsEnabled,
    isInstallAppPopupEnabled,
    isSportsSearchEnabled,
    isSportsWidgetEnabled,
    isBiggestWinnersCarouselEnabled,
    isRecentWinnersCarouselEnabled,
    isGameStatisticsEnabled,
    isGoogleButtonActive,
    isFacebookButtonActive,
    isTwitchButtonActive,
    isSteamButtonActive,
    isSocialAuthEnabled,
    isLudopathyValidationEnabled,
    authConfigsFromApi,
    isAccountSetLimitsEnabled,
    isAccountTimeoutLimitsEnabled,
    isCookieConsentPopUpEnabled,
    isNewAuthButtonsEnabled,
    isNewAuthButtonsPillEnabled,
    isNewDepositButtonPillEnabled,
    isDemoModeEnabled,
    isRTPEnabled,
    nomenclatureForCasinoGames,
    brandContacts,
    companyData,
    isHomeAsSportbookEnabled,
    isMaintenanceModeEnabled,
    isHomeNoticeEnabled,
    isGamesPaused,
    isDepositPaused,
    isRegisterPaused,
    isCheckinPaused,
    isRoulettePaused,
    isSiteShutdown,
    isContractsSectionEnabled,
    isUserMigrationEnabled,
    userMigrationNewDomain,
    userMigrationDate,
    isWithinMigrationPeriod,
  };
}
